/**
 * TESTIMONIALS — SINGLE SOURCE OF TRUTH
 * ---------------------------------------------------------------------------
 * Every testimonial read/write in the app goes through this module so the
 * moderation rules can never drift between the public wall, the homepage
 * strip, the submit form and the admin queue.
 *
 * Data lives in `mkt_testimonials` (Postgres + RLS):
 *   - Anyone may INSERT, but only with status='pending', featured=false and
 *     permission=true. A visitor cannot self-approve or self-feature.
 *   - Anyone may SELECT rows that are status='approved' AND permission=true.
 *     Authors additionally see their own pending rows; admins see everything.
 *   - Only admins may UPDATE (approve / reject / feature) or DELETE.
 * The rules are enforced in the database, not in this file — the client-side
 * filters below are for ordering and UX only.
 */
import { supabase } from '@/lib/supabase';
import { Lang } from '@/data/constants';

export type TestimonialStatus = 'pending' | 'approved' | 'rejected';
export type CustomerType = 'homeowner' | 'contractor';

export interface Testimonial {
  id: string;
  user_id?: string | null;
  name: string;
  city: string;
  state?: string | null;
  customer_type: CustomerType;
  rating: number;
  body: string;
  recommend: boolean;
  permission: boolean;
  language: Lang;
  status: TestimonialStatus;
  featured: boolean;
  email?: string | null;
  trade?: string | null;
  admin_note?: string | null;
  source?: string | null;
  created_at: string;
  reviewed_at?: string | null;
}

export interface TestimonialDraft {
  name: string;
  city: string;
  state?: string;
  customer_type: CustomerType;
  rating: number;
  body: string;
  recommend: boolean;
  permission: boolean;
  language: Lang;
  email?: string;
  user_id?: string | null;
  source?: string;
}

const TABLE = 'mkt_testimonials';

/** Columns that are safe to project publicly (never email / admin_note). */
const PUBLIC_COLUMNS =
  'id,name,city,state,customer_type,rating,body,recommend,language,status,featured,created_at';

/** The five-star scale used by the form, the cards and the admin queue. */
export const RATING_VALUES = [1, 2, 3, 4, 5] as const;

export const CUSTOMER_TYPES: { value: CustomerType; enKey: string }[] = [
  { value: 'homeowner', enKey: 'testi.typeHomeowner' },
  { value: 'contractor', enKey: 'testi.typeContractor' },
];

/** "John Michaels" -> "John M." so a public wall never exposes a full name. */
export const displayName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || name.trim();
  const last = parts[parts.length - 1];
  return `${parts.slice(0, -1).join(' ')} ${last.charAt(0).toUpperCase()}.`;
};

/** "Riverside" + "CA" -> "Riverside, CA" (state optional). */
export const displayLocation = (city: string, state?: string | null): string =>
  state ? `${city}, ${state}` : city;

export interface ValidationResult {
  ok: boolean;
  /** Translation keys for the fields that failed, in form order. */
  errors: Record<string, string>;
}

/**
 * Shared validation. Used by the public form AND re-checked by the database
 * CHECK constraints, so a crafted REST call cannot store a 1-character review.
 */
export const validateTestimonial = (d: Partial<TestimonialDraft>): ValidationResult => {
  const errors: Record<string, string> = {};
  const name = (d.name || '').trim();
  const city = (d.city || '').trim();
  const body = (d.body || '').trim();

  if (name.length < 2) errors.name = 'testi.errName';
  if (city.length < 2) errors.city = 'testi.errCity';
  if (!d.rating || d.rating < 1 || d.rating > 5) errors.rating = 'testi.errRating';
  if (body.length < 10) errors.body = 'testi.errBody';
  if (body.length > 2000) errors.body = 'testi.errBodyLong';
  if (!d.permission) errors.permission = 'testi.errPermission';
  if (d.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(d.email.trim())) errors.email = 'testi.errEmail';

  return { ok: Object.keys(errors).length === 0, errors };
};

/** Public read: approved reviews only. `featuredOnly` powers the homepage. */
export const fetchTestimonials = async (opts: {
  limit?: number;
  featuredOnly?: boolean;
  language?: Lang | 'all';
  customerType?: CustomerType | 'all';
} = {}): Promise<Testimonial[]> => {
  const { limit = 24, featuredOnly = false, customerType = 'all' } = opts;
  let q = supabase
    .from(TABLE)
    .select(PUBLIC_COLUMNS)
    .eq('status', 'approved')
    .order('featured', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(limit);

  if (featuredOnly) q = q.eq('featured', true);
  if (customerType !== 'all') q = q.eq('customer_type', customerType);

  const { data, error } = await q;
  if (error || !data) return [];
  return data as unknown as Testimonial[];
};

/**
 * Homepage strip: prefer curated (featured) reviews, but fall back to the most
 * recent approved ones so the section is never an empty box before an admin
 * has had time to feature anything.
 */
export const fetchHomepageTestimonials = async (limit = 3): Promise<Testimonial[]> => {
  const featured = await fetchTestimonials({ limit, featuredOnly: true });
  if (featured.length >= limit) return featured;
  const recent = await fetchTestimonials({ limit: limit * 2 });
  const seen = new Set(featured.map((f) => f.id));
  return [...featured, ...recent.filter((r) => !seen.has(r.id))].slice(0, limit);
};

/** Aggregate rating for the wall header and the AggregateRating schema. */
export const summarize = (rows: Testimonial[]) => {
  if (!rows.length) return { count: 0, average: 0, recommendPct: 0 };
  const total = rows.reduce((s, r) => s + (r.rating || 0), 0);
  const rec = rows.filter((r) => r.recommend).length;
  return {
    count: rows.length,
    average: Math.round((total / rows.length) * 10) / 10,
    recommendPct: Math.round((rec / rows.length) * 100),
  };
};

/** Public submit. Always lands as `pending` — the DB refuses anything else. */
export const submitTestimonial = async (
  d: TestimonialDraft
): Promise<{ error?: string }> => {
  const check = validateTestimonial(d);
  if (!check.ok) return { error: 'invalid' };

  const { error } = await supabase.from(TABLE).insert({
    user_id: d.user_id || null,
    name: d.name.trim(),
    city: d.city.trim(),
    state: (d.state || '').trim() || null,
    customer_type: d.customer_type,
    rating: d.rating,
    body: d.body.trim(),
    recommend: d.recommend,
    permission: true,
    language: d.language,
    email: (d.email || '').trim().toLowerCase() || null,
    source: d.source || 'public-form',
    status: 'pending',
    featured: false,
  });
  return error ? { error: error.message } : {};
};

// ---------------------------------------------------------------------------
// ADMIN. These calls only succeed for an admin JWT (RLS), so a non-admin who
// imports this module and calls them gets a database error, not a moderation.
// ---------------------------------------------------------------------------

export const fetchAllTestimonials = async (
  status: TestimonialStatus | 'all' = 'pending'
): Promise<Testimonial[]> => {
  let q = supabase.from(TABLE).select('*').order('created_at', { ascending: false }).limit(200);
  if (status !== 'all') q = q.eq('status', status);
  const { data } = await q;
  return (data || []) as Testimonial[];
};

export const setTestimonialStatus = async (id: string, status: TestimonialStatus) => {
  const patch: Record<string, unknown> = { status, reviewed_at: new Date().toISOString() };
  // A rejected review can never remain on the homepage.
  if (status !== 'approved') patch.featured = false;
  const { error } = await supabase.from(TABLE).update(patch).eq('id', id);
  return { error: error?.message };
};

export const setTestimonialFeatured = async (id: string, featured: boolean) => {
  const { error } = await supabase
    .from(TABLE)
    .update({ featured, ...(featured ? { status: 'approved' } : {}) })
    .eq('id', id);
  return { error: error?.message };
};

export const deleteTestimonial = async (id: string) => {
  const { error } = await supabase.from(TABLE).delete().eq('id', id);
  return { error: error?.message };
};
